import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import {showBasicHelpModal} from 'help/Modals';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';
import {WorklistType, worklistTypeDisplayName} from 'worklists/WorklistConstants';

import BarcodeScanningHeader from './BarcodeScanningHeader';
import ScannedWorklistItemsTable from './ScannedWorklistItemsTable';
import useBarcodes from './useBarcodes';
import useWorklistItemsFromBarcodeScanning from './useWorklistItemsFromBarcodeScanning';

function scannedIdentifierName(worklistType: WorklistType): string {
  return worklistType === WorklistType.BIOENTITY ? 'registry ID' : 'barcode';
}

function openScanWorklistItemsHelp(e: ElementMouseEvent, worklistType: WorklistType): void {
  showBasicHelpModal(
    e,
    `${capitalize(worklistTypeDisplayName(worklistType))} Scanning`,
    <div>
      <h4>Setup</h4>
      <p>
        For {worklistTypeDisplayName(worklistType)} scanning to work, your scanner must be configured to add a
        new line after every scan. Although each scanner's setup is different, almost all of them should
        support this configuration.
      </p>

      <p>
        In the manual for your scanner, look for a setting related to adding enter or a new line after each
        scan. Other models may call extra input at the end of the scan the "suffix", and have an option to
        make enter/return the suffix.
      </p>

      <p>
        If your model of scanner cannot be configured to add a new line after each scan, please contact us.
      </p>

      <h4>Testing</h4>
      <p>
        You can test that your scanner is configured correctly by scanning into an empty word document. You
        should see that the cursor moves down to the next line (just like hitting enter/return) after each
        scan.
      </p>
    </div>
  );
}

export interface ScanIntoWorklistModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (worklistItemIds: Array<string>) => void;
  worklistType: WorklistType;
}

/**
 * Modal to allow a user to scan or paste in barcodes that resolve to worklist items.
 */
export default function ScanIntoWorklistModal(props: ScanIntoWorklistModalProps): JSX.Element {
  const [barcodes, textareaProps, deleteBarcode] = useBarcodes();
  const {visibleScannedWorklistItems, removeScannedWorklistItem, hasVisibleDuplicateMatches} =
    useWorklistItemsFromBarcodeScanning(barcodes, props.worklistType);

  const modalTitleText =
    props.worklistType === WorklistType.BIOENTITY
      ? 'Scan or paste registry IDs'
      : `Scan or paste ${worklistTypeDisplayName(props.worklistType)} barcodes`;
  const modalTitle = (
    <div>
      {modalTitleText}
      <a
        onClick={(e) => {
          openScanWorklistItemsHelp(e, props.worklistType);
        }}
        className="u-tiny-spacing-left"
      >
        <BenchlingIcon icon="circle-question" />
      </a>
    </div>
  );

  const resolvedWorklistItemIds = _.compact(
    visibleScannedWorklistItems.map((item) => (item ? item.apiId : null))
  );
  const pluralizedWorklistTypeName = pluralize(2, worklistTypeDisplayName(props.worklistType));
  const scannedIdentifier = scannedIdentifierName(props.worklistType);
  return (
    <Modal
      title={modalTitle}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(resolvedWorklistItemIds);
      }}
      buttons={[
        {
          submit: true,
          text: `Use Scanned ${capitalize(pluralizedWorklistTypeName)}`,
          disabled: resolvedWorklistItemIds.length < 1,
        },
      ]}
      dataTestAttrs={{component: 'ScanIntoWorklistModal', element: 'root'}}
    >
      <div className={css(styles.wrapper)}>
        <BarcodeScanningHeader {...textareaProps} worklistType={props.worklistType} />
        <ScannedWorklistItemsTable
          visibleScannedWorklistItems={visibleScannedWorklistItems}
          handleRemoveRow={(scannedWorklistItem) => {
            removeScannedWorklistItem(scannedWorklistItem);
            // Even though there may be multiple worklist items per identifier, deleting this is fine because
            // removing from `barcodes` doesn't affect what's already rendered
            deleteBarcode(scannedWorklistItem.scannedIdentifier);
          }}
          worklistType={props.worklistType}
        />
      </div>
      {hasVisibleDuplicateMatches && (
        <div className={css(styles.warning)}>
          <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
          {`Warning: Some ${pluralize(
            2,
            scannedIdentifier
          )} matched multiple ${pluralizedWorklistTypeName}. You should remove any matches you
          didn't intend before proceeding. (This happens when ${pluralizedWorklistTypeName} with the same ${scannedIdentifier} exist in multiple
          registries).`}
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: '20px',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  warning: {
    fontSize: 13,
  },
  warningIcon: {
    color: Colors.ORANGE_LIGHT,
    marginRight: '5px',
  },
});
